import React, {useContext} from 'react'
import styled from '@emotion/styled'

import {Button} from '@vio/atlas/Button'
import {Box} from '@vio/atlas/helpers/Box'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {getRoomLabels} from '@vio/core/src/offer/business/getRoomLabels'
import {SearchOffer} from '@vio/core/src/offer/types/SearchOffer'
import {hexToRGBAString} from '@vio/core/src/utils/css'

import {OfferLayout} from '../../search/OfferLayout'
import {PriceSize} from '../../search/Price'
import {PriceComposition} from '../../search/PriceComposition'
import {ProviderLogo} from '../../search/ProviderLogo'
import {MirroredIcon} from '../MirroredIcon'
import {AlternativeOffersContext} from './AlternativeOffersContext'

const CtaWrapper = styled.div`
  margin-top: ${cssTheme.layout.spacing.s300};
  white-space: nowrap;
`

type BaseOfferProps = {
  offer: SearchOffer
}

const PrimaryWrapper = styled.section`
  border: 1px solid;
  border-color: ${cssTheme.colors.border.brand.tertiary};
  background: ${hexToRGBAString(
    cssTheme.colors.background.brand.tertiary,
    cssTheme.opacity.o50
  )};
  border-radius: ${cssTheme.layout.radius.md};
  padding: ${cssTheme.layout.spacing.s300} ${cssTheme.layout.spacing.s400}
    ${cssTheme.layout.spacing.s400} ${cssTheme.layout.spacing.s400};

  /* Just to have the constancy between old offers designs and new offers */
  margin: 0 -1px;
  &.--hover {
    cursor: pointer;
  }

  border-top-left-radius: 0;
  border-top-right-radius: 0;
  border-top: none;
`

const OfferLabels: React.FC<{offer: SearchOffer}> = ({offer}) => {
  const labels = getRoomLabels({
    offer,
    shouldShowYourChoice: false
  })

  return (
    <>
      {labels.map((label, index) => (
        <React.Fragment key={label.id}>
          <FormattedMessageWrapper {...label} />
          {index !== labels.length - 1 && ' · '}
        </React.Fragment>
      ))}
    </>
  )
}

const OfferPrice = ({offer, size}: {offer: SearchOffer; size?: PriceSize}) => {
  const {showTotalPrices} = useContext(AlternativeOffersContext)

  return (
    <PriceComposition
      size={size}
      price={showTotalPrices ? offer.totalRate : offer.nightlyRate}
      anchorPrice={0}
      currencyCode={offer.currency}
    />
  )
}

export const Primary: React.FC<BaseOfferProps> = ({offer}) => {
  return (
    <PrimaryWrapper>
      <OfferLayout
        variant="PrimaryMultiColumn"
        providerLogoComponent={
          <ProviderLogo providerCode={offer.intermediaryProvider} />
        }
        roomNameComponent={<Text variant="labelS">{offer.roomName}</Text>}
        roomLabelsComponent={
          <Text colorPath="content.success.secondary" variant="bodyXS">
            <OfferLabels offer={offer} />
          </Text>
        }
        priceComponent={<OfferPrice offer={offer} size="xl" />}
        ctaComponent={
          <CtaWrapper>
            <Button>
              <FormattedMessageWrapper
                id="viewDeal"
                defaultMessage="View deal"
              />
            </Button>
          </CtaWrapper>
        }
      />
    </PrimaryWrapper>
  )
}

export const Secondary: React.FC<BaseOfferProps> = ({offer}) => {
  return (
    <OfferLayout
      variant="SecondaryDefault"
      providerLogoComponent={
        <ProviderLogo providerCode={offer.intermediaryProvider} />
      }
      roomLabelsComponent={
        <Text colorPath="content.neutral.secondary" variant="bodyXS">
          <OfferLabels offer={offer} />
        </Text>
      }
      priceComponent={<OfferPrice offer={offer} size="sm" />}
      ctaComponent={
        <Box paddingLeft="s200">
          <MirroredIcon
            name={'ChevronRight'}
            size="sm"
            colorPath="content.neutral.secondary"
          />
        </Box>
      }
    />
  )
}
